import { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { ArrowLeft, User, BarChart3, LogOut, Download, Shield, Trash2, Calendar, MessageSquareHeart } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { getMonthlyLimit } from "@/lib/tier-limits";
import type { User as SupabaseUser } from "@supabase/supabase-js";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { trackSettingsViewed, trackLogout } from "@/lib/analytics";
import { BillingDetails } from "@/components/BillingDetails";
import { UsernameEditor } from "@/components/UsernameEditor";
import { Capacitor } from "@capacitor/core";
import { App as CapApp } from "@capacitor/app";
import { useAppUpdate } from "@/hooks/useAppUpdate";
import { RefreshCw } from "lucide-react";

const Settings = () => {
  const navigate = useNavigate();
  const isNative = Capacitor.isNativePlatform();
  const { updateAvailable, storeVersion, currentVersion, lastError, openStore, recheck } = useAppUpdate();
  const [installedVersion, setInstalledVersion] = useState<string>("");
  const [checkingUpdate, setCheckingUpdate] = useState(false);
  const [user, setUser] = useState<SupabaseUser | null>(null);
  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [fullName, setFullName] = useState("");
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showGracePeriod, setShowGracePeriod] = useState(false);
  const [billingRefreshKey, setBillingRefreshKey] = useState(0);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollCountRef = useRef(0);

  useEffect(() => {
    if (!isNative) return;
    CapApp.getInfo().then((i) => setInstalledVersion(i.version)).catch(() => {});
  }, [isNative]);

  const handleCheckForUpdates = async () => {
    setCheckingUpdate(true);
    try {
      const result = await recheck();
      if (result.updateAvailable) {
        toast.success(
          `Update available${result.storeVersion ? ` (v${result.storeVersion})` : ""}`,
        );
      } else if (result.lastError) {
        toast.error(`Couldn't check for updates: ${result.lastError}`);
      } else {
        toast.success("You're on the latest version");
      }
    } finally {
      setCheckingUpdate(false);
    }
  };


  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    pollCountRef.current = 0;
  }, []);

  const pollForTierChange = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const { data } = await supabase
        .from("profiles")
        .select("subscription_tier")
        .eq("id", session.user.id)
        .single();

      if (data && data.subscription_tier !== 'free') {
        stopPolling();
        toast.success(`Upgraded to ${data.subscription_tier}!`);
        loadUserData();
        setBillingRefreshKey(prev => prev + 1);
      } else {
        pollCountRef.current++;
        if (pollCountRef.current >= 10) {
          stopPolling();
        }
      }
    } catch {
      // ignore polling errors
    }
  }, [stopPolling]);

  const startPollingIfFree = useCallback(() => {
    if (pollingRef.current) return;
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) return;
      supabase.from("profiles").select("subscription_tier").eq("id", session.user.id).single().then(({ data }) => {
        if (data?.subscription_tier === 'free') {
          pollCountRef.current = 0;
          pollingRef.current = setInterval(pollForTierChange, 3000);
          pollForTierChange();
        }
      });
    });
  }, [pollForTierChange]);

  useEffect(() => {
    loadUserData();
    trackSettingsViewed();

    // Check for fromPayment URL param (works on web + native mobile)
    const params = new URLSearchParams(window.location.search);
    if (params.get('fromPayment') === 'true') {
      // Remove param from URL without reload
      const url = new URL(window.location.href);
      url.searchParams.delete('fromPayment');
      window.history.replaceState({}, '', url.pathname + url.search);
      // Start polling immediately
      startPollingIfFree();
    }

    // Visibilitychange listener for web tab-switching flow
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        startPollingIfFree();
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      stopPolling();
    };
  }, [pollForTierChange, stopPolling, startPollingIfFree]);

  const loadUserData = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate("/auth");
        return;
      }

      setUser(session.user);

      const { data, error } = await supabase
        .from("profiles")
        .select("full_name, username, subscription_tier, bonus_generations, total_generations, designs_this_month, created_at, marketing_emails_consent, account_deletion_requested_at, account_deletion_scheduled_for")
        .eq("id", session.user.id)
        .single();

      if (error) throw error;
      setProfile(data);
      setFullName(data?.full_name || "");
      setMarketingConsent(data?.marketing_emails_consent || false);
    } catch (error: any) {
      console.error("Error loading user data:", error);
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    if (user) {
      trackLogout(user.id);
    }
    await supabase.auth.signOut();
    navigate("/");
  };

  const handleUpdateProfile = async () => {
    try {
      const { error } = await supabase
        .from("profiles")
        .update({ 
          full_name: fullName,
          marketing_emails_consent: marketingConsent 
        })
        .eq("id", user?.id);

      if (error) throw error;

      await supabase.from("audit_logs").insert({
        user_id: user?.id,
        action: "profile_update",
        entity_type: "profile",
        entity_id: user?.id,
        details: { fields_updated: ["full_name", "marketing_emails_consent"] },
      });

      toast.success("Profile updated successfully");
      loadUserData();
    } catch (error: any) {
      console.error("Error updating profile:", error);
      toast.error("Failed to update profile");
    }
  };

  const handleExportData = async () => {
    setIsExporting(true);
    try {
      const { data, error } = await supabase.functions.invoke("export-user-data");

      if (error) throw error;

      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `decor-canvas-data-${new Date().toISOString().split("T")[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success("Data exported successfully");
    } catch (error: any) {
      console.error("Error exporting data:", error);
      toast.error("Failed to export data");
    } finally {
      setIsExporting(false);
    }
  };

  const handleDeleteAccount = async (gracePeriod: boolean) => {
    setIsDeleting(true);
    try {
      const { data, error } = await supabase.functions.invoke("delete-user-account", {
        body: { gracePeriod },
      });

      if (error) throw error;

      if (gracePeriod) {
        toast.success("Account deletion scheduled for 30 days from now. You can cancel anytime.");
        loadUserData();
      } else {
        toast.success("Account deleted successfully");
        await supabase.auth.signOut();
        navigate("/");
      }
    } catch (error: any) {
      console.error("Error deleting account:", error);
      toast.error("Failed to delete account");
    } finally {
      setIsDeleting(false);
      setShowGracePeriod(false);
    }
  };

  const handleCancelDeletion = async () => {
    try {
      const { error } = await supabase
        .from("profiles")
        .update({
          account_deletion_requested_at: null,
          account_deletion_scheduled_for: null,
        })
        .eq("id", user?.id);

      if (error) throw error;

      toast.success("Account deletion cancelled");
      loadUserData();
    } catch (error: any) {
      console.error("Error cancelling deletion:", error);
      toast.error("Failed to cancel deletion");
    }
  };

  const getTierLimit = (tier: string): number | string => {
    if (tier === 'enterprise') return 'Unlimited';
    return getMonthlyLimit(tier);
  };

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'pro': return 'bg-primary';
      case 'business': return 'bg-secondary';
      case 'enterprise': return 'bg-accent';
      default: return 'bg-muted';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-background flex items-center justify-center">
        <p className="text-muted-foreground">Loading settings...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-background">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-background/80 backdrop-blur-sm border-b border-border safe-area-inset-top">
        <div className="container mx-auto px-4 py-4">
          <Button variant="ghost" onClick={() => navigate("/designer")}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Designer
          </Button>
        </div>
      </header>

      <div className="container mx-auto px-3 sm:px-4 py-6 sm:py-12 max-w-4xl">
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 sm:mb-8 bg-gradient-hero bg-clip-text text-transparent">
          Account Settings
        </h1>

        {/* Profile Card */}
        <Card className="p-4 sm:p-6 shadow-card mb-4 sm:mb-6">
          <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
            <div className="bg-gradient-card rounded-full w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center">
              <User className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">{profile?.full_name || "User"}</h2>
              <p className="text-muted-foreground">{user?.email}</p>
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Account Created</p>
              <p className="font-medium">{new Date(profile?.created_at).toLocaleDateString()}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">Account ID</p>
              <p className="font-mono text-xs">{user?.id.slice(0, 16)}...</p>
            </div>
          </div>
        </Card>


        {/* Billing Details - Show for all users to display payment history */}
        {profile && <BillingDetails userTier={profile.subscription_tier || 'free'} refreshKey={billingRefreshKey} />}

        {/* Generation Credits Card */}
        <Card className="p-6 shadow-card mb-6">
          <div className="flex items-center gap-3 mb-6">
            <BarChart3 className="w-6 h-6 text-primary" />
            <h3 className="text-xl font-bold">Credits</h3>
          </div>

          <div className="space-y-4">
            <div className="flex justify-between items-center p-4 border rounded-lg">
              <div>
                <p className="font-medium">Available Credits</p>
                <p className="text-sm text-muted-foreground">
                  {profile?.bonus_generations || 0} credits remaining
                </p>
                <p className="text-xs text-muted-foreground/70 mt-1">(1 credit = 1 variation)</p>
              </div>
              <Badge className="text-lg">{profile?.bonus_generations || 0}</Badge>
            </div>
            
            <div className="flex justify-between items-center p-4 border rounded-lg">
              <div>
                <p className="font-medium">Current Plan</p>
                <p className="text-sm text-muted-foreground capitalize">{profile?.subscription_tier}</p>
              </div>
              <Badge variant="secondary">{profile?.subscription_tier}</Badge>
            </div>
            
            <div className="pt-4 border-t">
              <p className="text-sm text-muted-foreground">
                Total Lifetime: <span className="font-bold">{profile?.total_generations || 0}</span> designs generated
              </p>
            </div>
          </div>
        </Card>

        {/* Profile Management Card */}
        <Card className="p-6 shadow-card mb-6">
          <div className="flex items-center gap-3 mb-6">
            <User className="w-6 h-6 text-primary" />
            <h3 className="text-xl font-bold">Profile Information</h3>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="fullName">Full Name</Label>
              <Input
                id="fullName"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Your full name"
              />
            </div>

            {user && (
              <UsernameEditor
                currentUsername={profile?.username || null}
                userId={user.id}
                onUsernameChange={(newUsername) => setProfile((p: any) => ({ ...p, username: newUsername }))}
              />
            )}

            <div className="flex items-center justify-between py-2">
              <div className="space-y-0.5">
                <Label htmlFor="marketing">Marketing Emails</Label>
                <p className="text-sm text-muted-foreground">
                  Receive updates about new features and promotions
                </p>
              </div>
              <Switch
                id="marketing"
                checked={marketingConsent}
                onCheckedChange={setMarketingConsent}
              />
            </div>

            <Button onClick={handleUpdateProfile} className="w-full">
              Save Changes
            </Button>
          </div>
        </Card>

        {/* Data Management Card */}
        <Card className="p-6 shadow-card mb-6">
          <div className="flex items-center gap-3 mb-6">
            <Download className="w-6 h-6 text-primary" />
            <h3 className="text-xl font-bold">Data Management</h3>
          </div>

          <div className="space-y-4">
            <div>
              <h4 className="font-semibold mb-2">Download Your Data</h4>
              <p className="text-sm text-muted-foreground mb-4">
                Export all your personal information, designs, and activity history in JSON format.
              </p>
              <Button
                onClick={handleExportData}
                disabled={isExporting}
                variant="outline"
                className="w-full"
              >
                {isExporting ? "Exporting..." : "Download My Data"}
              </Button>
            </div>
          </div>
        </Card>

        {/* Feedback Card */}
        <Card className="p-6 shadow-card mb-6">
          <div className="flex items-center gap-3 mb-4">
            <MessageSquareHeart className="w-6 h-6 text-primary" />
            <h3 className="text-xl font-bold">Feedback & Testimonials</h3>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            Share your experience, leave a testimonial, or rate Decor Canvas.
          </p>
          <Button
            onClick={() => navigate("/feedback-and-testimonials?source=settings")}
            variant="outline"
            className="w-full"
          >
            <MessageSquareHeart className="w-4 h-4 mr-2" />
            Share Feedback
          </Button>
        </Card>

        {/* Privacy & Consents Card */}
        <Card className="p-6 shadow-card mb-6">
          <div className="flex items-center gap-3 mb-6">
            <Shield className="w-6 h-6 text-primary" />
            <h3 className="text-xl font-bold">Privacy & Consent</h3>
          </div>

          <div className="space-y-4">
            <div>
              <p className="text-sm text-muted-foreground mb-3">
                Manage your privacy preferences and view your consent history.
              </p>
              <div className="space-y-2">
                <Link to="/cookie-settings">
                  <Button variant="outline" className="w-full justify-start">
                    Manage Cookie Preferences
                  </Button>
                </Link>
                <Link to="/privacy">
                  <Button variant="outline" className="w-full justify-start">
                    View Privacy Policy
                  </Button>
                </Link>
                <Link to="/terms">
                  <Button variant="outline" className="w-full justify-start">
                    View Terms of Service
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </Card>

        {/* Usage Stats Card */}
        <Card className="p-6 shadow-card mb-6">
          <div className="flex items-center gap-3 mb-6">
            <BarChart3 className="w-6 h-6 text-primary" />
            <h3 className="text-xl font-bold">Usage Statistics</h3>
          </div>

          <div className="space-y-6">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Designs This Month</p>
              <p className="text-2xl font-bold">{profile?.designs_this_month || 0}</p>
            </div>

            <div>
              <p className="text-sm text-muted-foreground mb-1">Total Lifetime Generations</p>
              <p className="text-3xl font-bold text-primary">{profile?.total_generations || 0}</p>
            </div>
          </div>
        </Card>

        {/* App Updates (mobile only) */}
        {isNative && (
          <Card className="p-6 shadow-card mb-6">
            <div className="flex items-center gap-3 mb-4">
              <Download className="w-6 h-6 text-primary" />
              <h3 className="text-xl font-bold">App Version</h3>
            </div>
            <div className="space-y-2 mb-4">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Installed</span>
                <span className="font-medium" translate="no">v{installedVersion || currentVersion || "—"}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Latest available</span>
                <span className="font-medium" translate="no">
                  {storeVersion ? `v${storeVersion}` : updateAvailable ? "Update available" : "No update detected"}
                </span>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleCheckForUpdates}
                disabled={checkingUpdate}
                className="flex-1"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${checkingUpdate ? "animate-spin" : ""}`} />
                Check for updates
              </Button>
              {updateAvailable && (
                <Button onClick={openStore} className="flex-1">
                  Update now
                </Button>
              )}
            </div>
            
          </Card>
        )}

        {/* Account Actions */}
        <Card className="p-6 shadow-card">
          <h3 className="text-xl font-bold mb-4">Account Actions</h3>
          
          {profile?.account_deletion_scheduled_for && (
            <div className="mb-4 p-4 bg-destructive/10 border border-destructive rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="h-4 w-4 text-destructive" />
                <p className="font-semibold text-destructive">Account Deletion Scheduled</p>
              </div>
              <p className="text-sm text-muted-foreground mb-3">
                Your account is scheduled for deletion on{" "}
                {new Date(profile.account_deletion_scheduled_for).toLocaleDateString()}
              </p>
              <Button
                onClick={handleCancelDeletion}
                variant="outline"
                size="sm"
                className="w-full"
              >
                Cancel Deletion
              </Button>
            </div>
          )}

          <div className="space-y-3">
            <Button 
              onClick={handleSignOut}
              variant="outline"
              className="w-full justify-start"
            >
              <LogOut className="mr-2 h-4 w-4" />
              Sign Out
            </Button>

            <Separator />

            {!profile?.account_deletion_scheduled_for && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button 
                    variant="destructive"
                    className="w-full justify-start"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete Account
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete Your Account?</AlertDialogTitle>
                    <AlertDialogDescription asChild>
                      <div className="space-y-4">
                        <p>
                          This will permanently delete your account and remove all your data including 
                          saved designs, uploaded images, and activity history. This action cannot be undone.
                        </p>
                        <div className="p-3 bg-muted rounded-lg border">
                          <p className="text-sm font-medium flex items-center gap-2 mb-2">
                            <span>💡</span>
                            We recommend downloading your data first
                          </p>
                          <p className="text-xs text-muted-foreground mb-3">
                            Save your designs before proceeding with deletion.
                          </p>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.preventDefault();
                              handleExportData();
                            }}
                            disabled={isExporting}
                            className="w-full"
                          >
                            <Download className="mr-2 h-4 w-4" />
                            {isExporting ? "Exporting..." : "Download My Data"}
                          </Button>
                        </div>
                      </div>
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter className="flex-col sm:flex-row gap-2">
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowGracePeriod(false);
                        handleDeleteAccount(true);
                      }}
                      disabled={isDeleting}
                    >
                      Schedule Deletion (30 days)
                    </Button>
                    <AlertDialogAction
                      onClick={() => handleDeleteAccount(false)}
                      disabled={isDeleting}
                      className="bg-destructive hover:bg-destructive/90"
                    >
                      {isDeleting ? "Deleting..." : "Delete Immediately"}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Settings;
